/* import __COLOCATED_TEMPLATE__ from './conversation-stream.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type TaskInstance, type TaskGenerator } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import type InboxApi from 'embercom/services/inbox-api';
import { action } from '@ember/object';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { ResponseError } from 'embercom/lib/inbox/requests';

interface Args {
  selectedConversationId: number;
  conversationIds: number[];
  onConversationSelected: (conversationId: number) => void;
  totalHits: number;
  loadMoreConversations: () => void;
  isStandalone: boolean;
  selectedConversationCXExplanation: string | undefined;
  selectedConversationCXScore: number | undefined;
  selectedConversationCXAttributes: string | undefined;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
  hideCXWhenMissing?: boolean;
}

export const CX_REASON_TO_COLOR = {
  'product_feedback:positive': {
    color: 'green',
  },
  'product_feedback:negative': {
    color: 'red',
  },
  'feature_request:true': {
    color: '',
  },
  'policy_feedback:negative': {
    color: 'red',
  },
  'policy_feedback:positive': {
    color: 'green',
  },
  'user_effort:high': {
    color: 'red',
  },
  'user_effort:low': {
    color: 'green',
  },
  'user_strong_emotion:positive': {
    color: 'green',
  },
  'user_strong_emotion:negative': {
    color: 'red',
  },
  'customer_impact:positive': {
    color: 'green',
  },
  'customer_impact:negative': {
    color: 'red',
  },
  'answer_quality_bot:low': {
    color: 'red',
  },
  'answer_quality_bot:high': {
    color: 'green',
  },
  'answer_quality_teammate:low': {
    color: 'red',
  },
  'answer_quality_teammate:high': {
    color: 'green',
  },
};

export default class ConversationStream extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare inboxApi: InboxApi;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare reportingChartService: ReportingChartService;
  @tracked hideConversationEvents = true;
  @tracked private failedConversationId: number | null = null;
  fetchDataTask = taskFor(this.fetchConversation);
  conversation = trackedTask(this, this.fetchDataTask, () => [
    this.args.selectedConversationId,
  ]) as TaskInstance<Conversation | undefined>;

  private cachedConversations: Record<number, Conversation> = {};

  get conversationLoadFailed() {
    return this.failedConversationId === this.args.selectedConversationId;
  }

  @restartableTask
  *fetchConversation(): TaskGenerator<Conversation | undefined> {
    let conversation: Conversation | undefined =
      this.cachedConversations[this.args.selectedConversationId];
    if (!conversation) {
      try {
        conversation = yield this.inboxApi.fetchConversation(this.args.selectedConversationId);
        if (conversation) {
          this.cachedConversations[this.args.selectedConversationId] = conversation;
          this.failedConversationId = null;
        }
      } catch (error: unknown) {
        this.failedConversationId = this.args.selectedConversationId;
        // Don't cache failed fetches — let the next navigation attempt retry
        if (!(error instanceof ResponseError && error.response.status === 404)) {
          console.error('Error fetching conversation', error);
        }
      }
    }
    return conversation;
  }

  get currentIndex() {
    return this.args.conversationIds.indexOf(this.args.selectedConversationId);
  }

  get app() {
    return this.appService.app;
  }

  @action
  onPreviousConversation() {
    this.args.onConversationSelected(this.args.conversationIds[this.currentIndex - 1]);
    this.args.trackAnalyticsEvent('previous-conversation', 'conversation-stream', {
      conversationId: this.args.conversationIds[this.currentIndex - 1],
    });
  }

  @action
  onNextConversation() {
    let nextIndex = this.currentIndex + 1;
    if (nextIndex >= this.args.conversationIds.length && nextIndex < this.args.totalHits) {
      this.args.loadMoreConversations();
    } else {
      this.args.onConversationSelected(this.args.conversationIds[nextIndex]);
    }
    this.args.trackAnalyticsEvent('next-conversation', 'conversation-stream', {
      conversationId: this.args.conversationIds[nextIndex],
    });
  }

  @action
  toggleHideConversationEvents() {
    this.hideConversationEvents = !this.hideConversationEvents;
  }

  @action
  trackViewInInboxClick() {
    this.args.trackAnalyticsEvent('view-in-inbox', 'conversation-stream', {
      conversationId: this.args.selectedConversationId,
    });
  }

  get toggleConversationEventsTooltip() {
    this.args.trackAnalyticsEvent('toggle-conversation-events', 'conversation-stream', {
      hideConversationEvents: this.hideConversationEvents,
    });
    return this.hideConversationEvents
      ? this.intl.t(
          'automation.ai-insights.conversation-explore-panel.conversation-stream.show-conversation-events',
        )
      : this.intl.t(
          'automation.ai-insights.conversation-explore-panel.conversation-stream.hide-conversation-events',
        );
  }

  get allCxAttributes(): { label: string; color: string }[] {
    let availableValues = this.reportingUnderlyingDataService.getAttributeKnownValues(
      'conversation_custom_fields#customer_experience_attributes',
    );

    if (this.reportingChartService.useSyntheticData) {
      let drillInAttributes = this.args.selectedConversationCXAttributes;
      if (!drillInAttributes) {
        return [];
      }
      let labelToCode = Object.fromEntries(
        Object.entries(availableValues).map(([code, label]) => [label, code]),
      );
      return drillInAttributes
        .split(',')
        .map((label) => label.trim())
        .filter((label) => label.length > 0)
        .map((label) => {
          let rawCode = labelToCode[label];
          let colorConfig = rawCode
            ? CX_REASON_TO_COLOR[rawCode as keyof typeof CX_REASON_TO_COLOR]
            : undefined;
          return {
            label,
            color: colorConfig?.color || '',
          };
        });
    }

    if (!this.conversation) {
      return [];
    }
    let attributes = this.conversation.value?.attributesById?.['customer_experience_attributes']
      ?.value as string;
    if (!attributes) {
      return [];
    }
    return attributes.split('\n').map((attribute) => {
      let colorConfig = CX_REASON_TO_COLOR[attribute as keyof typeof CX_REASON_TO_COLOR];
      return {
        label: availableValues[attribute] as string,
        color: colorConfig?.color || '',
      };
    });
  }

  get viewInInboxTooltip() {
    return this.intl.t(
      'automation.ai-insights.conversation-explore-panel.conversation-stream.view-in-inbox',
    );
  }

  get viewInConversationsTooltip() {
    return this.intl.t(
      'automation.ai-insights.conversation-explore-panel.conversation-stream.view-in-conversations',
    );
  }

  get previousConversationTooltip() {
    return this.intl.t(
      'automation.ai-insights.conversation-explore-panel.conversation-stream.previous-conversation',
    );
  }

  get nextConversationTooltip() {
    return this.intl.t(
      'automation.ai-insights.conversation-explore-panel.conversation-stream.next-conversation',
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::ConversationStream': typeof ConversationStream;
    'reporting/automation/ai-insights/conversation-stream': typeof ConversationStream;
  }
}
